import React, { useState } from "react";
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import Popup from "reactjs-popup";

import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";
import { STATUS_OPTIONS, documentNoun, pillLabel, statusBadgeClass } from "./statusLabels";
import OrganizationAutocomplete from "./OrganizationAutocomplete";

const initialsFor = (name) => {
  if (!name || !name.trim()) return "—";
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
};

const avatarStyle = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: "#136395",
  color: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
};

const editableStyle = {
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
};

const numberMask = createNumberMask({
  prefix: "",
  decimalLimit: 0,
  allowLeadingZeroes: false,
  allowNegative: false,
  includeThousandsSeparator: false,
});

const popupContentStyle = {
  border: "none",
  borderRadius: 8,
  boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  padding: 0,
  background: "var(--sl-bg-primary, #fff)",
};

const dropdownStyle = {
  backgroundColor: "var(--sl-bg-primary, #fff)",
  borderRadius: 8,
  minWidth: 160,
  padding: 4,
};

const RentalInvoiceInfoCard = () => {
  const {
    rentalInvoiceFormState,
    setRentalInvoiceFormState,
    team,
    crmEnabled,
    csrfToken,
    organizationCustomFieldDefinitions,
  } = useRentalInvoiceEditContext();

  const [editingTitle, setEditingTitle] = useState(false);
  const [editingPayee, setEditingPayee] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(false);
  const [draftAttendance, setDraftAttendance] = useState("");

  const status = rentalInvoiceFormState.status;
  const eyebrow = `RENTAL ${documentNoun(status).toUpperCase()}`;
  const payeeName = rentalInvoiceFormState.payee_name || "";
  const clientName = rentalInvoiceFormState.organization?.name || payeeName;
  const clientLogoUrl = rentalInvoiceFormState.organization?.avatar_thumb_url;
  const attendance = rentalInvoiceFormState.attendance_count ?? 0;

  const handleEditKeydown = (e) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.target.blur();
    }
  };

  const activateOnKey = (e, enter) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      enter();
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="setlist-text-xs setlist-font-semibold figma-text link mb-2"
             style={{ letterSpacing: "0.08em" }}>
          {eyebrow}
        </div>

        <div className="d-flex align-items-center" style={{ gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingTitle ? (
              <input
                autoFocus
                type="text"
                maxLength={255}
                className="form-control setlist-display-xs setlist-font-semibold"
                placeholder="Untitled rental event"
                value={rentalInvoiceFormState.title || ""}
                onChange={(e) => setRentalInvoiceFormState({ ...rentalInvoiceFormState, title: e.target.value })}
                onBlur={() => setEditingTitle(false)}
                onKeyDown={handleEditKeydown}
              />
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={() => setEditingTitle(true)}
                onKeyDown={(e) => activateOnKey(e, () => setEditingTitle(true))}
                className="d-flex align-items-center"
                style={{ ...editableStyle, minWidth: 0 }}
              >
                <span
                  className={`setlist-display-xs setlist-font-semibold ${rentalInvoiceFormState.title ? "" : "figma-text quinary"}`}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {rentalInvoiceFormState.title || "Untitled rental event"}
                </span>
                <i className="far fa-pen ml-2 setlist-text-md figma-text quinary" style={{ flexShrink: 0 }} />
              </div>
            )}
          </div>

          <Popup
            arrow={false}
            position="bottom right"
            contentStyle={popupContentStyle}
            trigger={
              <span role="button"
                    tabIndex={0}
                    className={`badge ${statusBadgeClass(status)} clickable px-3 py-2 setlist-text-sm setlist-font-semibold`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "currentColor", opacity: 0.7 }} />
                {pillLabel(status)}
                <i className="far fa-chevron-down" />
              </span>
            }
          >
            {close => (
              <div style={dropdownStyle}>
                <ul className="list-group offer-inline-menu" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {STATUS_OPTIONS.map(({ value, label }) => (
                    <li key={value} className="list-group-item border-0" style={{ padding: 0 }}>
                      <a href="#"
                         className="d-block px-3 py-2 text-dark setlist-text-sm"
                         style={{ textDecoration: "none" }}
                         onClick={(e) => {
                           e.preventDefault();
                           close();
                           setRentalInvoiceFormState({ ...rentalInvoiceFormState, status: value });
                         }}>
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Popup>
        </div>

        <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            {clientLogoUrl ? (
              <img src={clientLogoUrl} alt={clientName} style={{ ...avatarStyle, objectFit: "cover" }} />
            ) : (
              <span style={avatarStyle}>{initialsFor(clientName)}</span>
            )}
            {editingPayee ? (
              crmEnabled ? (
                <OrganizationAutocomplete
                  teamId={team.id}
                  team={team}
                  csrfToken={csrfToken}
                  customFieldDefinitions={organizationCustomFieldDefinitions}
                  initialQuery={rentalInvoiceFormState.organization?.name || ""}
                  placeholder="Search organizations…"
                  onPick={({ organizationId, organizationName, organizationAvatarUrl }) => {
                    setRentalInvoiceFormState({
                      ...rentalInvoiceFormState,
                      organization_id: organizationId,
                      organization: { id: organizationId, name: organizationName, avatar_thumb_url: organizationAvatarUrl },
                    });
                    setEditingPayee(false);
                  }}
                  onCancel={() => setEditingPayee(false)}
                />
              ) : (
                <input
                  autoFocus
                  type="text"
                  maxLength={255}
                  className="form-control"
                  style={{ width: 240 }}
                  placeholder="Add client name"
                  value={payeeName}
                  onChange={(e) => setRentalInvoiceFormState({ ...rentalInvoiceFormState, payee_name: e.target.value })}
                  onBlur={() => setEditingPayee(false)}
                  onKeyDown={handleEditKeydown}
                />
              )
            ) : (
              <span
                role="button"
                tabIndex={0}
                onClick={() => setEditingPayee(true)}
                onKeyDown={(e) => activateOnKey(e, () => setEditingPayee(true))}
                className={`setlist-text-md setlist-font-semibold ${clientName ? "" : "figma-text quinary"}`}
                style={editableStyle}
              >
                {clientName || "Add client name"}
              </span>
            )}
          </div>

          <span className="figma-text quinary">·</span>

          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <i className="far fa-user-friends figma-text quaternary setlist-text-md" />
            {editingAttendance ? (
              <MaskedInput
                autoFocus
                className="form-control"
                style={{ width: 100 }}
                mask={numberMask}
                value={draftAttendance}
                onChange={(e) => setDraftAttendance(e.target.value)}
                onBlur={() => {
                  setRentalInvoiceFormState({
                    ...rentalInvoiceFormState,
                    attendance_count: parseInt(draftAttendance, 10) || 0
                  });
                  setEditingAttendance(false);
                }}
                onKeyDown={handleEditKeydown}
              />
            ) : (
              <span
                role="button"
                tabIndex={0}
                onClick={() => { setDraftAttendance(String(attendance)); setEditingAttendance(true); }}
                onKeyDown={(e) => activateOnKey(e, () => { setDraftAttendance(String(attendance)); setEditingAttendance(true); })}
                className={`setlist-text-md ${attendance > 0 ? "" : "figma-text quinary"}`}
                style={editableStyle}
              >
                <strong className="mr-1">{attendance}</strong> guests expected
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalInvoiceInfoCard;
